import React from 'react';
import { Link, Router } from 'react-router-dom';
import {
  Button,
  Card,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';

import UnauthNav from '../../components/UnauthNav/UnauthNav';

import './RegisterView.scss';
import { Login } from '@mui/icons-material';
import UserApi from '../../api/UserApi';
const RegisterView = () => {
  const [loading, setLoading] = React.useState(false);

  const [password, setPassword] = React.useState<string | null>(null);
  const [passwordConf, setPasswordConf] = React.useState<string | null>(null);
  const [email, setEmail] = React.useState<string | null>(null);
  const [firstName, setFirstName] = React.useState<string | null>(null);
  const [middleName, setMiddleName] = React.useState<string | null>(null);
  const [lastName, setLastName] = React.useState<string | null>(null);
  const [dob, setDob] = React.useState<string | null>(null);
  const [phoneNumber, setPhoneNumber] = React.useState<string | null>(null);
  const [tos, setTos] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const [success, setSuccess] = React.useState(false);
  const [resendLoading, setResendLoading] = React.useState(false);
  const [resendMessage, setResendMessage] = React.useState<string | null>(null);

  const registerReq = () => {
    setLoading(true);
    setErrorMessage(null);
    UserApi.register({
      User_Password: `${password}`,
      User_Email: `${email}`,
      User_FirstName: `${firstName}`,
      User_MiddleName: middleName || '',
      User_LastName: `${lastName}`,
      User_DOB: `${new Date(dob || '').toISOString().split('T')[0]}`, // Convert to ISO string
      User_TermsOfService: tos,
      User_PhoneNumber: phoneNumber || undefined,
    })
      .then((user) => {
        console.log('Registration successful', user);
        setSuccess(true);
        setLoading(false);
        // Handle successful registration, e.g., redirect to dashboard
      })
      .catch((error) => {
        console.error('Registration failed', error);
        setErrorMessage(
          error instanceof Error && error.message
            ? error.message
            : 'Something went wrong',
        );
        setLoading(false);
      });
  };

  const handleResendVerification = () => {
    if (!email) return;

    setResendLoading(true);
    setResendMessage(null);

    UserApi.resendVerification(email)
      .then((response) => {
        setResendMessage(
          response.message || 'Verification email sent successfully!',
        );
        setResendLoading(false);
      })
      .catch((error) => {
        setResendMessage('Verification email sent. Please check your inbox.');
        setResendLoading(false);
        // Always show success message to prevent email enumeration
      });
  };

  const passwordValid = () => {
    // If it does not contain at least one uppercase letter, one lowercase letter, one number, and one special character
    const lowercaseRegex = /[a-z]/;
    const uppercaseRegex = /[A-Z]/;
    const numberRegex = /\d/;
    const specialCharRegex = /[@.#$!%^&*.?]/;
    return (
      password !== null &&
      lowercaseRegex.test(password) &&
      uppercaseRegex.test(password) &&
      numberRegex.test(password) &&
      specialCharRegex.test(password) &&
      password.length >= 8 &&
      password.length <= 20
    );
  };

  const passwordConfValid = () => {
    return (
      passwordConf !== null &&
      password !== null &&
      password === passwordConf &&
      passwordConf.length > 0
    );
  };

  const emailValid = () => {
    // Simple email regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return email !== null && emailRegex.test(email);
  };

  const firstNameValid = () => {
    return firstName !== null && firstName.length > 0;
  };

  const lastNameValid = () => {
    return lastName !== null && lastName.length > 0;
  };

  const dobValid = () => {
    return (
      dob !== null && !isNaN(Date.parse(dob)) && Date.parse(dob) < Date.now()
    );
  };

  const phoneNumberValid = () => {
    // Basic phone validation - allows various formats
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    const cleanPhone = `${phoneNumber}`.replace(/[\s\-\(\)\.]/g, '');
    return phoneRegex.test(cleanPhone) && cleanPhone.length >= 10;
  };

  const valid = () => {
    return (
      passwordValid() &&
      passwordConfValid() &&
      emailValid() &&
      firstNameValid() &&
      lastNameValid() &&
      dobValid() &&
      phoneNumberValid() &&
      tos
    );
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !loading && valid()) {
      registerReq();
    }
  };

  if (success) {
    return (
      <>
        <UnauthNav />
        <div className="regv">
          <Card className="regv-card">
            <Typography variant="h1" className="regv-card-ttl">
              Registration Successful
            </Typography>

            {/* Success Message Section */}
            <div
              style={{
                backgroundColor: '#f0f8ff',
                padding: '24px',
                borderRadius: '8px',
                border: '1px solid #e3f2fd',
                marginBottom: '24px',
              }}
            >
              <Typography
                variant="h6"
                sx={{ mb: 2, color: '#1976d2', fontWeight: 600 }}
              >
                🎉 Welcome to TX Ticket Doctor!
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
                Your account has been created successfully! A verification email
                has been sent to <strong>{email}</strong>.
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', lineHeight: 1.5 }}
              >
                Please check your email and click the verification link to
                activate your account.
              </Typography>
            </div>

            {/* Email Verification Section */}
            <div
              style={{
                backgroundColor: '#fff8e1',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #ffcc02',
                marginBottom: '24px',
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ mb: 2, color: '#f57f17', fontWeight: 600 }}
              >
                📧 Didn't receive the email?
              </Typography>
              <Typography
                variant="body2"
                sx={{ mb: 2, color: '#f57f17', lineHeight: 1.5 }}
              >
                <strong>Please check your spam/junk folder first</strong> -
                verification emails sometimes end up there.
              </Typography>
              <Typography
                variant="body2"
                sx={{ mb: 3, color: '#f57f17', lineHeight: 1.5 }}
              >
                If you still don't see it, click the button below to send
                another verification email.
              </Typography>

              <Button
                variant="contained"
                onClick={handleResendVerification}
                disabled={resendLoading}
                startIcon={
                  resendLoading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <span>📤</span>
                  )
                }
                sx={{
                  backgroundColor: '#ff9800',
                  color: 'white',
                  fontWeight: 600,
                  px: 3,
                  py: 1.5,
                  '&:hover': {
                    backgroundColor: '#f57c00',
                  },
                  '&:disabled': {
                    backgroundColor: '#ffcc80',
                    color: 'white',
                  },
                }}
                fullWidth
              >
                {resendLoading ? 'Sending...' : 'Resend Verification Email'}
              </Button>

              {resendMessage && (
                <div
                  style={{
                    marginTop: '16px',
                    padding: '12px 16px',
                    backgroundColor: '#e8f5e8',
                    border: '1px solid #4caf50',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <span style={{ color: '#4caf50', fontSize: '16px' }}>✅</span>
                  <Typography
                    variant="body2"
                    sx={{ color: '#2e7d32', fontWeight: 500 }}
                  >
                    {resendMessage}
                  </Typography>
                </div>
              )}
            </div>

            {/* Action Buttons Section */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                alignItems: 'center',
              }}
            >
              <Button
                component={Link}
                to="/login"
                variant="contained"
                color="primary"
                size="large"
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  minWidth: '200px',
                }}
              >
                Continue to Login
              </Button>

              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', textAlign: 'center', mt: 1 }}
              >
                You can log in once you've verified your email address
              </Typography>
            </div>
          </Card>
        </div>
      </>
    );
  }

  return (
    <>
      <UnauthNav />
      <div className="regv">
        <Card className="regv-card">
          <Typography variant="h1" className="regv-card-ttl">
            Register
          </Typography>
          <TextField
            disabled={loading}
            autoFocus
            value={email}
            error={email !== null && !emailValid()}
            helperText={
              email !== null && !emailValid()
                ? 'Please enter a valid email address'
                : ''
            }
            required
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={handleKeyPress}
            label="Email"
            type="email"
            variant="outlined"
          />
          <TextField
            disabled={loading}
            value={password}
            error={password !== null && !passwordValid()}
            helperText={
              password !== null && !passwordValid()
                ? 'Password must be 8-20 characters, have one uppercase letter, one lowercase letter, one number, and one special character'
                : ''
            }
            required
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={handleKeyPress}
            label="Password"
            variant="outlined"
            type="password"
          />
          <TextField
            disabled={loading}
            value={passwordConf}
            error={passwordConf !== null && !passwordConfValid()}
            helperText={
              passwordConf !== null && !passwordConfValid()
                ? 'Passwords do not match'
                : ''
            }
            required
            onChange={(e) => setPasswordConf(e.target.value)}
            onKeyPress={handleKeyPress}
            label="Confirm Password"
            variant="outlined"
            type="password"
          />
          <TextField
            disabled={loading}
            value={firstName}
            error={firstName !== null && !firstNameValid()}
            helperText={
              firstName !== null && !firstNameValid()
                ? 'First name is required'
                : ''
            }
            required
            onChange={(e) => setFirstName(e.target.value)}
            onKeyPress={handleKeyPress}
            label="First Name"
            variant="outlined"
          />
          <TextField
            disabled={loading}
            value={middleName}
            onChange={(e) => setMiddleName(e.target.value)}
            onKeyPress={handleKeyPress}
            label="Middle Name"
            variant="outlined"
            helperText="Middle name or initial (optional)"
          />
          <TextField
            disabled={loading}
            value={lastName}
            error={lastName !== null && !lastNameValid()}
            helperText={
              lastName !== null && !lastNameValid()
                ? 'Last name is required'
                : ''
            }
            required
            onChange={(e) => setLastName(e.target.value)}
            onKeyPress={handleKeyPress}
            label="Last Name"
            variant="outlined"
          />
          {/* Create a date of birth field */}
          <TextField
            disabled={loading}
            value={dob}
            error={dob !== null && !dobValid()}
            helperText={
              dob !== null && !dobValid() ? 'Invalid date of birth' : ''
            }
            required
            onChange={(e) => setDob(e.target.value)}
            onKeyPress={handleKeyPress}
            label="Date of Birth"
            variant="outlined"
            type="date"
            InputLabelProps={{
              shrink: true,
            }}
          />
          <TextField
            disabled={loading}
            value={phoneNumber}
            error={phoneNumber !== null && !phoneNumberValid()}
            helperText={
              phoneNumber !== null && !phoneNumberValid()
                ? 'Invalid phone number format'
                : 'Phone number'
            }
            required
            onChange={(e) => setPhoneNumber(e.target.value)}
            onKeyPress={handleKeyPress}
            label="Phone Number"
            variant="outlined"
            type="tel"
            placeholder="(555) 123-4567"
          />
          <FormControlLabel
            disabled={loading}
            control={
              <>
                <Checkbox
                  checked={tos}
                  onChange={(e) => setTos(e.target.checked)}
                  color="primary"
                />
                <span>
                  I agree to the{' '}
                  <a
                    target="_blank"
                    href="https://txticketdr.com/terms-conditions"
                  >
                    Terms of Service
                  </a>
                </span>
              </>
            }
            label=""
          />
          {errorMessage && (
            <Typography variant="body2" color="error" className="regv-card-err">
              {errorMessage}
            </Typography>
          )}
          <Button
            disabled={!valid() || loading}
            onClick={registerReq}
            variant="contained"
            color="primary"
            startIcon={
              loading ? <CircularProgress size={20} color="inherit" /> : null
            }
          >
            {loading ? 'Creating Account...' : 'Register'}
          </Button>
          <hr />
          <Button
            disabled={loading}
            component={Link}
            to="/login"
            variant="contained"
            color="inherit"
            className="regv-card-regbtn"
          >
            Back to login
          </Button>
        </Card>
      </div>
    </>
  );
};
export default RegisterView;
